import { useCallback, useEffect, useId, useRef } from 'react';

import { useViewportSizeEffect } from 'hooks/viewport/useViewportSizeEffect';
import { transition } from 'theme/theme';

import { TabSwitcherTab } from './Tab';
import {
  TabSwitcherContainer,
  TabSwitcherIndicator,
  TabSwitcherTabsContainer,
} from './TabSwitcher.styled';
import { TabSwitcherTabData } from './TabSwitcherTabData';

type Props<T extends string> = {
  tabs: TabSwitcherTabData<T>[];
  value: T | undefined;
  onSelectTab: (name: T) => void;
  compact?: boolean;
};

export function TabSwitcher<T extends string>({
  tabs,
  value,
  onSelectTab,
  compact,
}: Props<T>) {
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLDivElement>(null);
  const selectedTabIndex = value
    ? tabs.findIndex((tab) => tab.value === value)
    : -1;

  const moveIndicator = useCallback(
    (animate?: boolean) => {
      const tabsContainer = tabsContainerRef.current;
      const indicator = indicatorRef.current;

      if (!tabsContainer || !indicator || selectedTabIndex === -1) return;

      // each tab has 2 elements, an input and a label
      const tabNode = tabsContainer.childNodes[selectedTabIndex * 2 + 1];
      if (!tabNode) return;

      const { offsetLeft, offsetWidth } = tabNode as HTMLDivElement;

      indicator.style.width = `${offsetWidth}px`;
      indicator.style.left = `${offsetLeft - tabsContainer.scrollLeft}px`;
      if (!animate) {
        indicator.style.transition = 'none';
      }
      setTimeout(() => {
        indicator.style.transition = transition;
      }, 0);
    },
    [selectedTabIndex],
  );

  useEffect(() => {
    moveIndicator(true);
  }, [moveIndicator]);

  useViewportSizeEffect(() => {
    moveIndicator(true);
  });

  const tabInputName = `tab-switcher-input-${useId()}`;

  return (
    <TabSwitcherContainer>
      <TabSwitcherTabsContainer
        ref={tabsContainerRef}
        onScroll={() => moveIndicator(false)}
      >
        {tabs.map((tab) => (
          <TabSwitcherTab
            key={tab.value}
            name={tabInputName}
            isSelected={tab.value === value}
            onChange={() => onSelectTab(tab.value)}
            compact={Boolean(compact)}
            qaId={tab.qaId}
          >
            {tab.label}
          </TabSwitcherTab>
        ))}
      </TabSwitcherTabsContainer>

      <TabSwitcherIndicator ref={indicatorRef} />
    </TabSwitcherContainer>
  );
}
